import {Event} from '@/apiTypes/event';
import {useAnalytics} from '@/hooks/useAnalytics';
import {getRouteTracksForEvent} from '@/server/event';
import {Download} from 'lucide-react';
import {useCallback, useEffect, useState} from 'react';
import {Typography} from '../ui/typography';
import {RouteHeader} from './routeHeader/routeHeader';
import {RouteMap} from './routeMap/routeMap';
import {Categories} from './categories/categories';
import {RouteSelector} from './routeSelector/routeSelector';
import {EventSection} from '../eventPage/eventSection';

export type EventRoute = NonNullable<Event['routes']>[number];
type CategoryWithGroup = NonNullable<Event['categories']>[number];

/** A route is considered displayable only if it carries some user-facing
 *  content beyond a bare name — description, distance, elevation, a
 *  downloadable file, or track geometry. Name alone is excluded because
 *  scrapers often assign generic defaults like "Main Route" that convey
 *  nothing to the user on their own. */
function hasMeaningfulRouteData(route: EventRoute): boolean {
  return Boolean(
    route.description ||
      route.distance_meters ||
      route.elevation_gain_meters ||
      route.routeFileUrl ||
      (route.track_points && route.track_points.length > 0)
  );
}

type GroupedCategories = {
  groupName: string | null;
  sortOrder: number;
  categories: CategoryWithGroup[];
};

/** Groups categories by their categoryGroup field and sorts them. */
export function groupCategories(
  categories: CategoryWithGroup[]
): GroupedCategories[] {
  const groupMap = new Map<string | null, CategoryWithGroup[]>();

  const sortedCategories = [...categories].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
  );

  for (const category of sortedCategories) {
    const group = category.categoryGroup;
    if (!groupMap.has(group)) {
      groupMap.set(group, []);
    }
    groupMap.get(group)!.push(category);
  }

  const groups: GroupedCategories[] = [];
  for (const [groupName, cats] of groupMap) {
    const sortOrder = cats[0].categoryGroupSortOrder || 0;
    groups.push({groupName, sortOrder, categories: cats});
  }

  return groups.sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Individual route card that fills available space. */
export function RouteCard({
  route,
  isSelected,
  hasMultiple,
  onSelect,
}: {
  route: EventRoute;
  isSelected: boolean;
  hasMultiple: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex-1 flex items-start min-w-0 text-left border rounded-lg p-4 transition-colors ${
        isSelected && hasMultiple
          ? 'border-gray-900 ring-1 ring-gray-900'
          : 'border-gray-200 hover:border-gray-300'
      } ${hasMultiple ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <RouteHeader route={route} />
    </button>
  );
}

/** Unified routes card with route cards, map, and download link. */
export function RoutesCard({
  event,
  selectedRouteId,
  onSelectRoute,
}: {
  event: Event;
  selectedRouteId: string | null;
  onSelectRoute: (id: string) => void;
}) {
  const {track} = useAnalytics();

  const handleSelectRoute = useCallback(
    (routeId: string) => {
      track('Route Selected', {
        event_id: event.id,
        event_name: event.name,
        route_id: routeId,
      });
      onSelectRoute(routeId);
    },
    [track, event.id, event.name, onSelectRoute]
  );
  const routes = event.routes || [];

  const [trackData, setTrackData] = useState<
    Record<
      string,
      {
        track_points: EventRoute['track_points'];
        waypoints: EventRoute['waypoints'];
      }
    >
  >({});
  const [tracksLoading, setTracksLoading] = useState(true);
  const [tracksError, setTracksError] = useState(false);

  useEffect(() => {
    setTrackData({});
    setTracksLoading(true);
    setTracksError(false);
    if (routes.length === 0) {
      setTracksLoading(false);
      return;
    }
    let cancelled = false;
    getRouteTracksForEvent({data: event.id})
      .then((tracks) => {
        if (cancelled) return;
        const lookup: typeof trackData = {};
        for (const t of tracks) {
          lookup[t.id] = {
            track_points: t.track_points,
            waypoints: t.waypoints,
          };
        }
        setTrackData(lookup);
        setTracksLoading(false);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to load route track data:', error);
          setTracksError(true);
          setTracksLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [event.id, routes.length]);

  const routesWithTracks = routes.map((route) => {
    const tracks = trackData[route.id];
    if (!tracks) return route;
    return {...route, ...tracks};
  });
  const allCategories = event.categories || [];

  // Show the Routes section only when at least one route has displayable
  // data. While tracks are still loading we optimistically show, since
  // track_points may be the only meaningful data on the route.
  const showRoutesSection =
    tracksLoading || routesWithTracks.some(hasMeaningfulRouteData);

  if (!showRoutesSection && allCategories.length === 0) {
    return null;
  }

  const selectedRoute =
    routesWithTracks.find((r) => r.id === selectedRouteId) ||
    routesWithTracks[0];
  const hasMultiple = routes.length > 1;

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* Routes section */}
        {showRoutesSection && (
          <EventSection
            label={hasMultiple ? 'Routes' : 'Route'}
            adornment={
              hasMultiple && (
                <Typography
                  level="B3"
                  className="flex-1 text-right text-neutral-500"
                >
                  {`${routes.length} route options available`}
                </Typography>
              )
            }
          >
            {hasMultiple && (
              <RouteSelector
                activeRoute={selectedRoute}
                onClick={handleSelectRoute}
                routes={routes}
              />
            )}

            {!hasMultiple && (
              <RouteHeader route={selectedRoute} showName={false} />
            )}
            <RouteMap
              loading={tracksLoading}
              error={tracksError}
              route={selectedRoute}
            />

            {selectedRoute?.routeFileUrl && (
              <a
                href={selectedRoute.routeFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-secondary-500 hover:underline"
              >
                <Download className="w-3 h-3" />
                Download GPX
              </a>
            )}
            <hr />
          </EventSection>
        )}

        {/* Category Section */}
        <Categories categories={selectedRoute.categories} />
      </div>
    </>
  );
}
